import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

type ErrorBoundaryProps = {
  children: ReactNode
  fallbackTitle?: string
  fallbackMessage?: string
  onRetry?: () => void
}

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
}

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  padding: theme.spacing(4),
  textAlign: 'center',
}))

const ErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}))

const ErrorTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}))

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
  maxWidth: 400,
}))

const ButtonsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}))

/**
 * Error Boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire app.
 *
 * @example
 * <ErrorBoundary
 *   fallbackTitle="Something went wrong"
 *   fallbackMessage="We couldn't load this page. Please try again."
 *   onRetry={() => window.location.reload()}
 * >
 *   <SomeComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = (): void => {
    const { onRetry } = this.props

    this.setState({ hasError: false, error: null })

    if (onRetry) {
      onRetry()
    }
  }

  render(): ReactNode {
    const { hasError } = this.state
    const { children, fallbackTitle, fallbackMessage } = this.props

    if (hasError) {
      return (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorTitle variant="h5">
            {fallbackTitle || 'Something went wrong'}
          </ErrorTitle>
          <ErrorMessage variant="body1">
            {fallbackMessage ||
              'An unexpected error occurred. Please try again or refresh the page.'}
          </ErrorMessage>
          <ButtonsRow>
            <Button variant="contained" color="primary" onClick={this.handleRetry}>
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.href = '/'}>
              Go to Home
            </Button>
          </ButtonsRow>
        </ErrorContainer>
      )
    }

    return children
  }
}

export default ErrorBoundary
